import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Row, Button, Select, Space } from "@masterteam/mt-design-react";

import Modal from "../Modal";
import CardList from "../CardList";
import NewTable from "../NewTable";
import AdminPage from "../AdminPage";
import DeleteModal from "../DeleteModal";
import ObjectiveWizard from "../ObjectiveWizard";

import api from "../../providers/api";
import { addToast } from "../../helpers";
import {
  getObjectives,
  updateObjective,
  deleteObjective,
} from "../../reducers/strategicObjective";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";

const initialModalState = {
  isOpen: false,
  data: null,
};

const StrategicObjectivesPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const [{ strategies, isLoading }, strategyTypes, objectiveLevel] =
    useSelector(({ strategicObjective, theme }) => [
      strategicObjective,
      theme.StrategyTypes.item.Items,
      theme.ObjectiveLevel.item.Items,
    ]);

  const [formModal, setFormModal] = useState(initialModalState);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(0);
  const [selectedObjective, setSelectedObjective] = useState(0);
  const [strategyType, setStrategyType] = useState(strategyTypes[0]);
  const [keyName, setKeyName] = useState("Themes");

  useEffect(() => {
    dispatch(getObjectives(+strategyType.Value));
  }, [strategyType]);

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.objective
      .delete(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          dispatch(
            deleteObjective({
              strategyIndex: selectedStrategy,
              id,
              type: strategyType?.Value,
            })
          );
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  return (
    <AdminPage
      title={intl.formatMessage({ id: "common.strategic-objectives" })}
    >
      <div style={{ width: "32.9%", marginBottom: "20px" }}>
        <Space>
          <Select
            options={strategyTypes}
            getOptionLabel={(el) => (isAr ? el.Arabic : el.English)}
            getOptionValue={(el) => el?.Value}
            onChange={(item) => {
              setStrategyType(item);
              setSelectedStrategy(0);
              setSelectedObjective(0);
              setKeyName(+item?.Value === 1 ? "Themes" : "Pillars");
            }}
            isLoading={isLoading}
            value={strategyType}
          />

          <Select
            options={strategies}
            getOptionLabel={(el) => (isAr ? el.ArabicName : el.EnglishName)}
            getOptionValue={(el) => el?.ID}
            onChange={(item) => {
              setSelectedStrategy(
                strategies?.findIndex((e) => e.ID === item.ID)
              );
              setSelectedObjective(0);
            }}
            isLoading={isLoading}
            value={strategies?.[selectedStrategy]}
          />
        </Space>
      </div>

      <Row size="large">
        <div style={{ flex: 1 }}>
          <CardList
            data={strategies?.[selectedStrategy]?.[keyName]}
            title={intl.formatMessage({
              id:
                +strategyType?.Value === 1 ? "common.themes" : "common.pillars",
            })}
            isLoading={isLoading}
            onClickItem={(item) => {
              setSelectedObjective(
                strategies?.[selectedStrategy]?.[keyName]?.findIndex(
                  (e) => e.ID === item.ID
                )
              );
            }}
            nameKey={isAr ? "ArabicName" : "EnglishName"}
            valueKey="ID"
            animation={isAr ? "fadeInRight" : "fadeInLeft"}
            selectedItem={
              strategies?.[selectedStrategy]?.[keyName]?.[selectedObjective]?.ID
            }
          />
        </div>
        <div
          className="animated fadeInRight"
          style={{ flex: 2, height: "calc(100vh - 350px)" }}
        >
          <NewTable
            data={
              strategies?.[selectedStrategy]?.[keyName]?.[selectedObjective]
                ?.Objectives
            }
            actionsHeader={
              <Button
                fixed
                onClick={() => setFormModal({ isOpen: true, data: null })}
              >
                {intl.formatMessage({ id: "common.add" })}
              </Button>
            }
            columns={[
              {
                key: "Strategic Objective",
                header: intl.formatMessage({
                  id: "common.strategic-objective",
                }),
                cell: (item) => (isAr ? item.ArabicName : item.EnglishName),
                width: "50%",
              },
              {
                key: "Level",
                header: intl.formatMessage({ id: "common.level" }),
                cell: (item) => {
                  const level = objectiveLevel.find(
                    (e) => +e.Value === item?.Level
                  );
                  return (isAr ? level?.Arabic : level?.English) || "---";
                },
                width: "25%",
              },
              {
                key: "Order",
                header: intl.formatMessage({ id: "common.order" }),
                cell: (item) => item.Order,
                width: "25%",
              },
            ]}
            actions={[
              {
                key: "edit",
                variant: "primary",
                Icon: EditIcon,
                "tooltip-position": "bottom",
                tooltip: intl.formatMessage({ id: "common.update" }),
                onClick: (item) => {
                  setFormModal({ isOpen: true, data: item });
                },
              },
              {
                key: "delete",
                variant: "error",
                Icon: DeleteIcon,
                "tooltip-position": "bottom",
                tooltip: intl.formatMessage({ id: "common.delete" }),
                onClick: (item) => {
                  setDeleteModal({ isOpen: true, data: item.ID });
                },
                disabled: (item) => !item.IsDeletable,
              },
            ]}
            compact
            scrollable
          />
        </div>
      </Row>

      {formModal?.isOpen && (
        <Modal isOpen={formModal?.isOpen}>
          <ObjectiveWizard
            bindObjective={(data) => {
              if (+strategyType?.Value === +data?.StrategyType)
                dispatch(updateObjective(data));
            }}
            editData={formModal.data}
            modeSaveOrUpdate={!formModal.data}
            closeModal={() => setFormModal(initialModalState)}
            selectedType={strategyType}
            selectedStrategy={strategies?.[selectedStrategy] || null}
          />
        </Modal>
      )}

      {deleteModal.isOpen && (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({ id: "common.strategic-objective" })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      )}
    </AdminPage>
  );
};

export default StrategicObjectivesPage;
